/* eslint-disable no-underscore-dangle */
import { useContext, useEffect, useMemo, useState } from 'react';
import Select, { StylesConfig } from 'react-select';
import { useDebouncedCallback } from 'use-debounce';
import { darkColors, FormLabel, SwitchThemeContext, theme } from '@tryhackme/thm-ui-components';
import { TagSelectorProps } from 'src/features/manage-room/components/categories-tab/components/tag-selector/tag-selector.types';
import { useGetTagsQuery } from 'src/features/admin/tagging/tagging.slice';
import { Tag, TagColourKey, TagColours } from 'src/features/admin';
import { StyledTagsSelector } from 'src/features/manage-room/components/categories-tab/categories-tab.styles';
import { setTagsListByCategory } from 'src/app/reducers/manage-room.reducer';
import { useAppDispatch } from 'src/app/hooks';
import { ThemeColorMode } from 'src/common/enums';
import { useFeatureIsOn } from 'src/common/growthbook';
import { StyledRemoveBtn, StyledSelectedTagRow, StyledTagLabel, StyledWeightDropdown } from './tag-selector.styles';
interface TagOption extends Tag {
  __isNew__?: boolean;
}
export const TagSelector = ({
  category,
  existingRoomTags
}: TagSelectorProps) => {
  const dispatch = useAppDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [searchLabel, setSearchLabel] = useState<string>('');
  const [debouncing, setDebouncing] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const setDebouncedSearchLabel = useDebouncedCallback((label: string) => {
    setSearchLabel(label);
    setDebouncing(false);
  }, 500);
  useEffect(() => {
    setSelectedTags(existingRoomTags.filter(tag => tag.category === category._id));
  }, [category._id, existingRoomTags]);
  const {
    _id: categoryId,
    label: categoryLabel
  } = category;
  const isWeightedTagsEnabled = useFeatureIsOn('weighted-room-tags');
  const {
    data: tagList,
    isLoading
  } = useGetTagsQuery({
    category: categoryId,
    populate: false,
    subcategory: undefined,
    label: searchLabel,
    limit: 100
  }, {
    refetchOnMountOrArgChange: true,
    skip: !isMenuOpen || !categoryId
  });
  const handleMenuOptionsChange = (catId: string, option: Tag) => {
    const newTag = option;
    if (isWeightedTagsEnabled) newTag.weight = 1;
    const options = [...selectedTags, newTag];
    dispatch(setTagsListByCategory({
      categoryId: catId,
      tags: options
    }));
    setSelectedTags(options);
  };
  const handleWeightChange = (tagId: string, catId: string, weight: number) => {
    const tagIndex = selectedTags.findIndex(tag => tag._id === tagId);
    if (tagIndex === -1) return;
    const updatedTag = ({
      ...selectedTags[tagIndex],
      weight
    } as Tag);
    const options = [...selectedTags];
    options[tagIndex] = updatedTag;
    dispatch(setTagsListByCategory({
      categoryId: catId,
      tags: options
    }));
    setSelectedTags(options);
  };
  const removeTag = (tagId: string, catId: string) => {
    const updatedTags = selectedTags.filter(selectedTag => selectedTag._id !== tagId);
    setSelectedTags(updatedTags);
    dispatch(setTagsListByCategory({
      categoryId: catId,
      tags: updatedTags
    }));
  };
  const onInputChange = (value: string) => {
    setDebouncing(true);
    setDebouncedSearchLabel(value);
  };
  const {
    theme: uiTheme
  } = useContext(SwitchThemeContext);
  const isDarkMode = uiTheme === ThemeColorMode.DARK;
  const overrideStyles: StylesConfig<TagOption, true> = useMemo(() => ({
    control: styles => ({
      ...styles,
      ...(isDarkMode && {
        backgroundColor: darkColors.background.dark,
        borderColor: darkColors.border.light,
        color: theme.colors.grey[400],
        ':hover': {
          borderColor: darkColors.border.light
        }
      })
    }),
    input: styles => ({
      ...styles,
      ...(isDarkMode && {
        color: theme.colors.white
      })
    }),
    menuList: styles => ({
      ...styles,
      ...(isDarkMode && {
        backgroundColor: darkColors.background.dark,
        borderColor: `1px solid ${darkColors.border.light}`
      })
    }),
    dropdownIndicator: styles => ({
      ...styles,
      ...(isDarkMode && {
        color: theme.colors.grey[400]
      })
    }),
    indicatorSeparator: styles => ({
      ...styles,
      ...(isDarkMode && {
        backgroundColor: darkColors.border.light
      })
    }),
    option: (styles, {
      data
    }) => {
      const optionBgColor = TagColours[(data.colour as TagColourKey)]?.backgroundColor ?? data.__isNew__ ? theme.colors.grey[100] : theme.colors.grey[400];
      return {
        ...styles,
        backgroundColor: isDarkMode ? darkColors.border.light : optionBgColor,
        color: TagColours[(data.colour as TagColourKey)]?.[isDarkMode ? 'darkModeFontColor' : 'fontColor'] || theme.colors[isDarkMode ? 'white' : 'black'],
        ':hover': {
          opacity: 0.75,
          cursor: 'pointer'
        },
        padding: data.__isNew__ ? 0 : '8px 12px'
      };
    }
  }), [isDarkMode]);
  const unselectedTags = tagList?.tags?.filter(tag => !selectedTags.some(selectedTag => selectedTag._id === tag._id));
  return <StyledTagsSelector data-testid={`tag-selector-${categoryId}`} data-sentry-element="StyledTagsSelector" data-sentry-component="TagSelector" data-sentry-source-file="tag-selector.tsx">
      <FormLabel isRequired htmlFor={categoryId} labelPosition="top" data-testid="tag-multi-select-label" data-sentry-element="FormLabel" data-sentry-source-file="tag-selector.tsx">
        {categoryLabel}
      </FormLabel>
      <Select placeholder="Add tag" isSearchable backspaceRemovesValue controlShouldRenderValue={false} tabSelectsValue isLoading={isLoading || debouncing} styles={overrideStyles} onMenuOpen={() => setIsMenuOpen(true)} onMenuClose={() => setIsMenuOpen(false)} onInputChange={onInputChange} onChange={value => handleMenuOptionsChange(categoryId, ((value as unknown) as Tag))} options={unselectedTags || []} getOptionValue={option => option._id} data-sentry-element="Select" data-sentry-source-file="tag-selector.tsx" />
      <div>
        {selectedTags.map(tag => <StyledSelectedTagRow key={tag._id}>
            <StyledTagLabel backgroundColor={TagColours[(tag.colour as TagColourKey)]?.backgroundColor} borderColor={TagColours[(tag.colour as TagColourKey)]?.borderColor} fontColor={TagColours[(tag.colour as TagColourKey)]?.fontColor}>
              {tag.label}
              <StyledRemoveBtn fontColor={TagColours[(tag.colour as TagColourKey)]?.fontColor} type="button" onClick={() => removeTag(tag._id, categoryId)} aria-label={`Remove ${tag.label}`}>
                &times;
              </StyledRemoveBtn>
            </StyledTagLabel>
            {isWeightedTagsEnabled && <StyledWeightDropdown>
                Weight:
                <select data-testid={`tag-weight-${tag._id}`} value={tag.weight ?? 1} onChange={e => handleWeightChange(tag._id, categoryId, Number(e.target.value))}>
                  {[1, 2, 3, 4, 5].map(weight => <option data-testid={`tag-weight-${tag._id}-${weight}`} key={weight} value={weight}>
                      {weight}
                    </option>)}
                </select>
              </StyledWeightDropdown>}
          </StyledSelectedTagRow>)}
      </div>
    </StyledTagsSelector>;
};